import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Clock, Truck, Wrench } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto, type OpcaoDeSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { BotaoDoRaro } from '@/componentes/LinhaDoPreenchido';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { rotuloDaEscolha } from '@/componentes/rotuloDaEscolha';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { useFrota } from '@/modulos/equipamentos/consultas-frota';
import { useOrdens } from '@/modulos/oficina/consultas';
import { numeroDaOrdem, ROTULO_DA_ETAPA } from '@/modulos/oficina/ordem';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarInteiro, formatarDia } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { codigoDaRecusa } from '@/lib/versao-da-ficha';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, fatiarPagina } from '@/modulos/painel/painel';
import { useSessao } from '@/modulos/sessao/consultas';

import { api, CODIGO_INSPECAO_EM_ANDAMENTO, type Desfecho, type Inspecao, type LinhaDeCobertura, type Ocorrencia } from './api';
import { RespostasDaInspecao } from './RespostasDaInspecao';
import {
  coberturaEscrita,
  criticidadeEscrita,
  desfechoEscrito,
  estadoDaFaixa,
  oQueFaltaParaEncerrar,
  oQueFicouAberto,
  porqueNaoDa,
  rotinaEscrita,
  totaisDaInspecao,
  checklistsDaMaquina,
} from './inspecao';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * MÓDULO 10 — checklists e ocorrências. O terceiro do ciclo 2.
 *
 * ## A tela abre na fila de ocorrências, não na lista de inspeções
 *
 * O v6 escreve dois riscos, e são a mesma doença de dois lados: *"checklist
 * preenchido apenas como formalidade"* e *"ocorrência crítica encerrada sem
 * tratamento"*. Um checklist é formalidade quando preenchê-lo é o fim do
 * assunto — e uma tela que abre na lista de formulários preenchidos é
 * exatamente isso: ela mostra o trabalho feito, não o problema pendente.
 *
 * Então a primeira coisa é **o que foi achado e ainda não foi tratado**, por
 * criticidade e com os dias em aberto. A lista de inspeções vem depois, e a
 * cobertura — quem não foi verificado — vem com ela.
 *
 * ## Concluir não encerra, e a tela diz isso em voz alta
 *
 * A estrutura já garante que a ocorrência não morre com o formulário: nada no
 * banco liga as duas situações. O que a tela deve é **não deixar quem concluiu
 * sair achando que terminou** — por isso a conclusão responde com quantas
 * ocorrências continuam abertas, e a frase aparece na tela.
 *
 * ## Sinalizar bloqueio não é parar a máquina
 *
 * A ferramenta 3 diz "sinaliza necessidade de bloqueio operacional", e
 * sinalizar é o verbo. Parar uma escavadeira é registrado em Operação, com a
 * causa escrita. A tela repete esse aviso onde a dúvida aparece, porque "pede
 * bloqueio" se lê facilmente como "foi bloqueada".
 */

const DESFECHOS: readonly Desfecho[] = ['ordem_de_servico', 'resolvida_no_local', 'nao_procede'];

export function PaginaInspecao() {
  const base = useId();
  const cliente = useQueryClient();
  const sessao = useSessao();
  const permissoes = useMemo(() => sessao.data?.permissoes ?? [], [sessao.data]);

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);
  const [abrindo, setAbrindo] = useState(false);
  const [equipamentoId, setEquipamentoId] = useState('');
  const [checklistId, setChecklistId] = useState('');
  /* O checklist que a cobertura escolheu, enquanto a pessoa não trocou. */
  const [checklistDoSistema, setChecklistDoSistema] = useState(false);
  const [verTodosOsChecklists, setVerTodosOsChecklists] = useState(false);
  const [encerrando, setEncerrando] = useState<Ocorrencia | null>(null);
  /* A evidência tem janela própria, e não um campo dentro do encerramento: ela
     se anexa ENQUANTO o achado está aberto, que é quando o campo tira a foto —
     e a janela de encerrar é o fim do assunto. */
  const [anexando, setAnexando] = useState<Ocorrencia | null>(null);
  const [descricaoDaEvidencia, setDescricaoDaEvidencia] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [desfecho, setDesfecho] = useState<Desfecho | null>(null);
  const [ordemId, setOrdemId] = useState('');
  const [justificativa, setJustificativa] = useState('');
  /* O recado da última conclusão: é ele que impede a pessoa de sair achando
     que terminou. Fica na tela até ela abrir outra inspeção. */
  const [recadoDaConclusao, setRecadoDaConclusao] = useState<string | null>(null);
  /* A inspeção aberta na gaveta de respostas (achado dos robôs, 20/10/2026):
     sem ela a mesa não tinha onde responder, e o "Concluir" recusava sempre. */
  const [respondendo, setRespondendo] = useState<Inspecao | null>(null);

  const ocorrencias = useQuery({ queryKey: ['inspecao', 'ocorrencias'], queryFn: api.ocorrencias });
  const inspecoes = useQuery({ queryKey: ['inspecao', 'inspecoes'], queryFn: api.inspecoes });
  const cobertura = useQuery({ queryKey: ['inspecao', 'cobertura'], queryFn: api.cobertura });
  const checklists = useQuery({ queryKey: ['inspecao', 'checklists'], queryFn: api.checklists });

  /* Máquina e O.S. saem da lista: as janelas pediam o identificador interno
     (achado no QAS, 20/10/2026). Cada lista é pedida com a sua janela aberta,
     e só por quem a lê. A máquina que já aparece na cobertura entra mesmo sem
     a frota — quem só inspeciona ainda escolhe destas. */
  const podeLerFrota = permissoes.includes('equipamento.ler');
  const podeLerOrdens = permissoes.includes('os.ler');
  const frota = useFrota(podeLerFrota && abrindo);
  const opcoesDeMaquina = useMemo(() => {
    /* A máquina da frota vem com a foto, o tipo e a obra (opcaoDaMaquina). */
    const porId = new Map<string, OpcaoDeSelecao>();
    for (const m of frota.data ?? []) if (m.ativo) porId.set(m.id, opcaoDaMaquina(m));
    for (const c of cobertura.data?.linhas ?? []) if (!porId.has(c.equipamentoId)) porId.set(c.equipamentoId, { valor: c.equipamentoId, rotulo: c.equipamento, adesivo: { id: c.equipamentoId, codigo: c.equipamento } });
    return [...porId.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
  }, [frota.data, cobertura.data]);
  const ordens = useOrdens(podeLerOrdens && encerrando !== null && desfecho === 'ordem_de_servico');
  const opcoesDeOrdem = useMemo(
    () =>
      (ordens.data ?? [])
        .filter((o) => o.equipamentoId === encerrando?.equipamentoId && o.etapa !== 'encerrada' && o.etapa !== 'cancelada')
        .map((o) => ({ valor: o.id, rotulo: `${numeroDaOrdem(o.numero)} · ${ROTULO_DA_ETAPA[o.etapa]} · ${o.sintoma}` }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [ordens.data, encerrando],
  );

  const fila = useMemo(() => ocorrencias.data?.ocorrencias ?? [], [ocorrencias.data]);
  const linhasDeCobertura = useMemo(() => cobertura.data?.linhas ?? [], [cobertura.data]);
  /* A cobertura inteira, paginada (p.26) — antes ela cortava em 50 linhas sem
     dizer, e a 51ª máquina não verificada não aparecia em lugar nenhum. */
  const paginaDaCobertura = usePaginacao(linhasDeCobertura);
  const paginaDasEmAndamento = usePaginacao((inspecoes.data?.inspecoes ?? []).filter((i) => i.situacao === 'em_andamento'));
  const totais = useMemo(() => totaisDaInspecao(fila, linhasDeCobertura), [fila, linhasDeCobertura]);
  const faixa = estadoDaFaixa(ocorrencias);
  const fatia = fatiarPagina(fila, pagina, porPagina);
  const publicados = useMemo(
    () => (checklists.data?.checklists ?? []).filter((c) => c.situacao === 'publicado'),
    [checklists.data],
  );

  /* OS CHECKLISTS DA MÁQUINA (31/10/2026): a cobertura diz quais valem para
     ela; os publicados que ela não cobre ficam atrás de um botão. */
  const daMaquina = useMemo(
    () => checklistsDaMaquina(linhasDeCobertura, equipamentoId).filter((id) => publicados.some((c) => c.id === id)),
    [linhasDeCobertura, equipamentoId, publicados],
  );
  const checklistsAVista =
    daMaquina.length === 0 || verTodosOsChecklists
      ? [...publicados].sort((a, b) => Number(daMaquina.includes(b.id)) - Number(daMaquina.includes(a.id)))
      : publicados.filter((c) => daMaquina.includes(c.id) || c.id === checklistId);
  const outrosChecklists = daMaquina.length === 0 ? 0 : publicados.length - daMaquina.length;
  const codigoDaMaquina =
    opcoesDeMaquina.find((o) => o.valor === equipamentoId)?.rotulo ?? 'a máquina';
  function escolherMaquina(id: string) {
    setEquipamentoId(id);
    setVerTodosOsChecklists(false);
    const cobre = checklistsDaMaquina(linhasDeCobertura, id).filter((c) => publicados.some((p) => p.id === c));
    if (cobre.length === 1) {
      setChecklistId(cobre[0] ?? '');
      setChecklistDoSistema(true);
    } else if (checklistDoSistema || (cobre.length > 0 && !cobre.includes(checklistId))) {
      setChecklistId('');
      setChecklistDoSistema(false);
    }
  }

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['inspecao'] });
  const fecharEncerramento = () => {
    setEncerrando(null);
    setDesfecho(null);
    setOrdemId('');
    setJustificativa('');
  };

  /* O "em quê" do retorno (R04, p.32), do que a tela já leu. */
  const daOcorrencia = (id: string) => (ocorrencias.data?.ocorrencias ?? []).find((o) => o.id === id);
  const daInspecao = (id: string) => (inspecoes.data?.inspecoes ?? []).find((i) => i.id === id);

  const abrirInspecao = useMutation({
    mutationFn: () => api.criarInspecao({ equipamentoId, modeloId: checklistId }),
    meta: {
      retorno: retornoDe(() => {
        const maquina = (cobertura.data?.linhas ?? []).find((c) => c.equipamentoId === equipamentoId);
        const checklist = (checklists.data?.checklists ?? []).find((c) => c.id === checklistId);
        return {
          titulo: 'Inspeção aberta',
          ...(maquina ? { alvo: { codigo: maquina.equipamento } } : {}),
          fatos: [checklist?.nome ?? null],
          efeito: 'Responda os itens e conclua; os achados vão para a fila de ocorrências',
        };
      }),
    },
    onSuccess: async () => {
      setAbrindo(false);
      setEquipamentoId('');
      setChecklistId('');
      setChecklistDoSistema(false);
      setVerTodosOsChecklists(false);
      setRecadoDaConclusao(null);
      await recarregar();
    },
  });
  /* A INSPECAO JA ABERTA HOJE (robos de 20/10/2026, rodada 2): o servidor
     recusa abrir a segunda com 409 `inspecao_em_andamento`, e a janela oferece
     CONTINUAR a que existe — deixada pelo campo ou por outra pessoa. */
  const jaAberta = codigoDaRecusa(abrirInspecao.error) === CODIGO_INSPECAO_EM_ANDAMENTO;
  const continuarAberta = async () => {
    const lidas = (await inspecoes.refetch()).data?.inspecoes ?? [];
    const nome = (checklists.data?.checklists ?? []).find((c) => c.id === checklistId)?.nome;
    const viva = lidas.find((i) => i.equipamentoId === equipamentoId && i.checklist === nome && i.situacao === 'em_andamento');
    if (!viva) return;
    abrirInspecao.reset();
    setAbrindo(false);
    setRespondendo(viva);
  };
  const concluir = useMutation({
    mutationFn: api.concluir,
    meta: {
      retorno: retornoDe<{ ocorrenciasAbertas: number; criticasAbertas: number }, string>((r, id) => {
        const i = daInspecao(id);
        return {
          titulo: 'Inspeção concluída',
          tom: r.criticasAbertas > 0 ? 'ressalva' : 'sucesso',
          ...(i ? { alvo: { codigo: i.equipamento, nome: i.checklist } } : {}),
          fatos: [
            r.ocorrenciasAbertas === 0 ? 'nenhuma ocorrência em aberto' : `${formatarInteiro(r.ocorrenciasAbertas)} em aberto`,
            r.criticasAbertas > 0 ? `${formatarInteiro(r.criticasAbertas)} ${r.criticasAbertas === 1 ? 'crítica' : 'críticas'}` : null,
          ],
          ...(r.ocorrenciasAbertas > 0 ? { efeito: 'As ocorrências abertas estão na fila' } : {}),
        };
      }),
    },
    onSuccess: async (resultado) => {
      /* A FRASE QUE O MÓDULO EXIGE — ver o cabeçalho. */
      setRecadoDaConclusao(oQueFicouAberto(resultado));
      setRespondendo(null);
      await recarregar();
    },
  });
  const sinalizar = useMutation({
    mutationFn: (p: { id: string; pede: boolean }) => api.sinalizarBloqueio(p.id, p.pede),
    meta: {
      retorno: retornoDe<{ aviso: string }, { id: string; pede: boolean }>((r, p) => {
        const o = daOcorrencia(p.id);
        return {
          titulo: p.pede ? 'Bloqueio da máquina pedido' : 'Pedido de bloqueio retirado',
          ...(o ? { alvo: { codigo: o.equipamento, nome: o.descricao } } : {}),
          ...(r.aviso ? { efeito: r.aviso } : {}),
        };
      }),
    },
    onSuccess: recarregar,
  });
  const encerrar = useMutation({
    mutationFn: (p: { id: string; desfecho: Desfecho; ordemId: string; justificativa: string }) =>
      api.encerrar(p.id, {
        desfecho: p.desfecho,
        ordemId: p.ordemId.trim() === '' ? null : p.ordemId,
        justificativa: p.justificativa,
      }),
    meta: {
      retorno: retornoDe<unknown, { id: string; desfecho: Desfecho; justificativa: string }>((_r, p) => {
        const o = daOcorrencia(p.id);
        return {
          titulo: 'Ocorrência encerrada',
          ...(o ? { alvo: { codigo: o.equipamento, nome: o.descricao } } : {}),
          fatos: [desfechoEscrito(p.desfecho), p.justificativa.trim() || null],
          efeito: 'Saiu da fila de ocorrências',
        };
      }),
    },
    onSuccess: async () => {
      fecharEncerramento();
      await recarregar();
    },
  });

  /* ------------------------------------------------- a fila de ocorrências */

  const COLUNAS: readonly Coluna<Ocorrencia>[] = [
    {
      chave: 'equipamento',
      rotulo: 'Máquina e achado',
      celula: (o) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{o.equipamento}</span>
          <TextoDaCelula
            texto={`${o.descricao}${o.checklist === null ? '' : ` · ${o.checklist}`}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    {
      chave: 'criticidade',
      rotulo: 'Criticidade',
      /* Texto escrito, e não selo: os seis status do manual descrevem uma
         máquina, e uma ocorrência não está "Parada". */
      celula: (o) => <span className="txt-corpo text-tinta">{criticidadeEscrita(o.criticidade)}</span>,
    },
    {
      chave: 'dias',
      rotulo: 'Dias em aberto',
      alinhamento: 'numero',
      celula: (o) =>
        /* Colorido só quando o próprio valor é o alerta (regra 3): crítica
           parada há mais de três dias. */
        o.criticidade === 'critico' && o.diasAberta > 3 ? (
          <span className="numero text-parada-texto">{formatarInteiro(o.diasAberta)}</span>
        ) : (
          <span className="numero text-tinta">{formatarInteiro(o.diasAberta)}</span>
        ),
    },
    {
      chave: 'bloqueio',
      rotulo: 'Pede bloqueio',
      prioridade: 'secundaria',
      celula: (o) =>
        o.pedeBloqueio ? (
          <span className="txt-corpo text-tinta">Sim</span>
        ) : (
          <span className="text-tinta-secundaria">—</span>
        ),
    },
    {
      chave: 'ordem',
      rotulo: 'O.S.',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (o) =>
        o.ordem === null ? (
          <span className="text-tinta-secundaria">—</span>
        ) : (
          <span className="codigo text-tinta">{formatarInteiro(o.ordem)}</span>
        ),
    },
  ];

  /* Encerrar e pedir bloqueio só para quem tem a licença de cada um (prova do
     marco, 07/10/2026): antes os dois apareciam desligados no menu, com a
     licença no motivo. `porqueNaoDa` diz qual licença falta. */
  const acoesDaOcorrencia = (o: Ocorrencia): readonly AcaoDoMenu[] => [
    /* Ver e anexar na MESMA ação: quem abre para anexar quase sempre quer
       ver o que já está lá, e duas entradas de menu para a mesma pasta de
       fotos é uma a mais para ler. */
    { rotulo: 'Fotos e observações', aoEscolher: () => setAnexando(o) },
    ...(porqueNaoDa('encerrar', permissoes) === undefined ? [{ rotulo: 'Encerrar ocorrência', aoEscolher: () => setEncerrando(o) }] : []),
    ...(porqueNaoDa('bloqueio', permissoes) === undefined
      ? [
          {
            rotulo: o.pedeBloqueio ? 'Retirar pedido de bloqueio' : 'Pedir bloqueio da máquina',
            aoEscolher: () => sinalizar.mutate({ id: o.id, pede: !o.pedeBloqueio }),
          },
        ]
      : []),
  ];

  /* ----------------------------------------------------------- a cobertura */

  const COLUNAS_DE_COBERTURA: readonly Coluna<LinhaDeCobertura>[] = [
    {
      chave: 'equipamento',
      rotulo: 'Máquina',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{l.equipamento}</span>
          <TextoDaCelula
            texto={`${l.checklist} · ${rotinaEscrita(l.rotina)}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    {
      chave: 'ultima',
      rotulo: 'Última inspeção',
      /* "Nunca inspecionada" por extenso: um travessão se leria como falta de
         dado, e o risco do v6 é máquina usada sem verificação registrada. */
      celula: (l) =>
        l.ultimaEm === null ? (
          <span className="txt-corpo text-parada-texto">{coberturaEscrita(l)}</span>
        ) : (
          <span className="txt-corpo text-tinta">{coberturaEscrita(l)}</span>
        ),
    },
    {
      chave: 'inspecoes',
      rotulo: 'Inspeções',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (l) => <span className="numero text-tinta">{formatarInteiro(l.inspecoes)}</span>,
    },
  ];

  const faltaParaEncerrar =
    encerrando === null
      ? 'Escolha a ocorrência.'
      : oQueFaltaParaEncerrar(encerrando, { desfecho, ordemId, justificativa });

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina flex flex-col gap-6">
        <CabecalhoPagina
          sobretitulo="Checklists e ocorrências"
          titulo="O que a inspeção achou"
          subtitulo="Os achados de campo que ainda não foram tratados, e quem ainda não foi verificado"
          acoes={
            permissoes.includes('inspecao.gravar') ? (
            <HistoriaNaMesa codigo="BTN-INS-02" className="flex">
              <JanelaDeAcao
                rotulo="Abrir inspeção"
                titulo="Abrir a inspeção de uma máquina"
                nivel="primario"
                aberta={abrindo}
                aoAbrir={() => setAbrindo(true)}
                aoFechar={() => setAbrindo(false)}
                acoes={
                  <>
                    <Botao nivel="terciario" onClick={() => setAbrindo(false)}>
                      Cancelar
                    </Botao>
                    <Botao
                      nivel="primario"
                      disabled={equipamentoId.trim() === '' || checklistId === '' || abrirInspecao.isPending}
                      onClick={() => abrirInspecao.mutate()}
                    >
                      {abrirInspecao.isPending ? 'Abrindo…' : 'Abrir inspeção'}
                    </Botao>
                  </>
                }
              >
                <div className="flex flex-col gap-4">
                  <p className="txt-apoio text-tinta-secundaria">
                    Só checklist publicado vira inspeção. Marcar um item como não conforme abre a
                    ocorrência na hora, e ela continua aberta depois de a inspeção ser concluída.
                  </p>
                  <CampoSelecao
                    rotulo="Máquina"
                    valor={equipamentoId}
                    aoMudar={escolherMaquina}
                    opcoes={opcoesDeMaquina}
                    adesivo={{ porQueFora: 'a máquina está desativada no cadastro' }}
                    rotuloVazio={rotuloDaEscolha({
                      consulta: podeLerFrota && opcoesDeMaquina.length === 0 ? frota : { isPending: false, isError: false },
                      pode: true,
                      quantas: opcoesDeMaquina.length,
                      escolha: 'Escolha a máquina',
                      nenhuma: podeLerFrota
                        ? 'Nenhuma máquina em uso · cadastre em Equipamentos'
                        : 'Nenhuma máquina à vista · o seu perfil não lê a frota, peça a liberação em Cadastros › Governança',
                      semPermissao: '',
                    })}
                  />
                  {checklists.isPending ? (
                    /* Carregando (p.32), e não "nenhum checklist": a legenda é
                       certa, e as opções, na forma da linha de escolha, são
                       esqueleto. */
                    <div className="flex flex-col gap-2">
                      <span className="txt-rotulo text-tinta">Checklist</span>
                      <Carregando oQue="Carregando os checklists publicados" className="flex flex-col gap-2">
                        {['w-1/2', 'w-3/5', 'w-2/5'].map((largura) => (
                          <span key={largura} className="flex items-center gap-2 py-1">
                            <Barra className="size-4 rounded-pill" />
                            <Barra className={`h-3 ${largura}`} />
                          </span>
                        ))}
                      </Carregando>
                    </div>
                  ) : publicados.length === 0 ? (
                    <p className="txt-apoio text-tinta-secundaria">
                      Nenhum checklist publicado ainda. Monte a lista e publique-a com a nota da
                      revisão técnica antes de inspecionar.
                    </p>
                  ) : (
                    <fieldset id={`${base}-checklists`} className="flex flex-col gap-2 border-0 p-0">
                      <legend className="flex flex-wrap items-baseline gap-x-1">
                        <span className="txt-rotulo text-tinta">Checklist</span>
                        {checklistDoSistema ? <span className="txt-ajuda">· pré-preenchido</span> : null}
                      </legend>
                      {/* OS DA MÁQUINA PRIMEIRO (31/10/2026): a cobertura já sabe
                          que checklist vale para ela; sendo um só, ele vem
                          escolhido. Os outros publicados ficam a um clique. */}
                      {checklistsAVista.map((c) => (
                        <label key={c.id} className="flex items-baseline gap-2">
                          <input
                            type="radio"
                            name={`${base}-checklist`}
                            value={c.id}
                            checked={checklistId === c.id}
                            onChange={() => {
                              setChecklistId(c.id);
                              setChecklistDoSistema(false);
                            }}
                          />
                          <span className="txt-corpo text-tinta">{c.nome}</span>
                          <span className="txt-apoio text-tinta-secundaria">
                            {`${rotinaEscrita(c.rotina)} · ${formatarInteiro(c.itens)} ${c.itens === 1 ? 'item' : 'itens'}, ${formatarInteiro(c.criticos)} ${c.criticos === 1 ? 'crítico' : 'críticos'}`}
                          </span>
                        </label>
                      ))}
                      {checklistDoSistema ? (
                        <span className="txt-ajuda">{`O checklist que cobre ${codigoDaMaquina}. Troque se for outro.`}</span>
                      ) : daMaquina.length > 0 && !verTodosOsChecklists ? (
                        <span className="txt-ajuda">{`Os que cobrem ${codigoDaMaquina}.`}</span>
                      ) : null}
                      {outrosChecklists > 0 ? (
                        <BotaoDoRaro
                          aberto={verTodosOsChecklists}
                          aoAlternar={() => setVerTodosOsChecklists((aberto) => !aberto)}
                          rotulo={`Ver ${outrosChecklists === 1 ? 'o outro checklist' : `os outros ${formatarInteiro(outrosChecklists)} checklists`}`}
                          rotuloAberto="Só os da máquina"
                          idDoConteudo={`${base}-checklists`}
                        />
                      ) : null}
                    </fieldset>
                  )}
                  {abrirInspecao.isError ? (
                    <div role="alert" className="flex flex-col items-start gap-2">
                      <p className="txt-apoio text-parada-texto">{explicarFalha(abrirInspecao.error).comoResolver}</p>
                      {jaAberta ? (
                        <Botao nivel="secundario" onClick={() => void continuarAberta()}>
                          Continuar inspeção
                        </Botao>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </JanelaDeAcao>
            </HistoriaNaMesa>
            ) : undefined
          }
        />

        {/* O RECADO DA CONCLUSÃO. Ele é o ponto da tela: sem ele, concluir o
            formulário se parece com terminar o assunto. */}
        {recadoDaConclusao === null ? null : (
          <div role="status" className="flex flex-wrap items-center justify-between gap-4 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="txt-corpo text-pretty text-tinta">{recadoDaConclusao}</span>
            <Botao nivel="terciario" onClick={() => setRecadoDaConclusao(null)}>
              Entendi
            </Botao>
          </div>
        )}

        {faixa === 'falhou' ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="flex flex-col gap-1">
              <span className="txt-corpo text-tinta">A fila de ocorrências não carregou.</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(ocorrencias.error).comoResolver}</span>
            </span>
            <Botao nivel="secundario" onClick={() => void ocorrencias.refetch()}>
              Carregar de novo
            </Botao>
          </div>
        ) : (
          /* Carregando (p.32): os quatro cards já se sabem — o rótulo e o ícone
             ficam, e só o número é esqueleto. */
          <FaixaIndicadores>
            <CardIndicador
              rotulo="Críticas em aberto"
              icone={Wrench}
              tom="parada"
              carregando={faixa === 'carregando'}
              valor={formatarInteiro(totais.criticasAbertas)}
              alerta={totais.criticasAbertas > 0}
              contexto="Achado crítico que ainda não foi tratado"
            />
            <CardIndicador
              historia="NUM-INS-01"
              rotulo="Ocorrências em aberto"
              icone={ClipboardList}
              tom="marca"
              carregando={faixa === 'carregando'}
              valor={formatarInteiro(totais.abertas)}
              contexto="Tudo que a inspeção achou e ninguém encerrou"
            />
            <CardIndicador
              rotulo="Mais antiga"
              icone={Clock}
              tom="standby"
              carregando={faixa === 'carregando'}
              valor={formatarInteiro(totais.maisAntigaEmDias)}
              unidade="dias"
              alerta={totais.maisAntigaEmDias > 7}
              contexto="Dias da ocorrência mais velha da fila"
            />
            <CardIndicador
              rotulo="Nunca inspecionadas"
              icone={Truck}
              tom="standby"
              /* Sai da cobertura, que é outra consulta: sem ela, o card diria
                 0 como se toda máquina tivesse sido inspecionada. */
              carregando={faixa === 'carregando' || cobertura.isPending}
              valor={formatarInteiro(totais.nuncaInspecionadas)}
              alerta={totais.nuncaInspecionadas > 0}
              contexto="Máquinas sem nenhuma inspeção do checklist publicado"
            />
          </FaixaIndicadores>
        )}

        <Tabela
          rotulo="Ocorrências em aberto"
          colunas={COLUNAS}
          linhas={fatia.visiveis}
          chaveDaLinha={(o) => o.id}
          carregando={ocorrencias.isPending}
          erro={
            ocorrencias.isError ? (
              <ErroDaLista oQue="As ocorrências" erro={ocorrencias.error} aoTentarDeNovo={() => void ocorrencias.refetch()} />
            ) : null
          }
          alturaDupla
          acoes={(o) => <MaisAcoes acoes={acoesDaOcorrencia(o)} />}
          vazio={
            <EstadoVazio
              titulo="Nenhuma ocorrência em aberto"
              descricao="Todos os achados das inspeções foram encerrados, por O.S., resolvidos no local ou descartados com a razão escrita."
            />
          }
          rodape={
            <RodapeTabela
              primeiro={fatia.primeiro}
              ultimo={fatia.ultimo}
              total={fila.length}
              pagina={fatia.pagina}
              paginas={fatia.paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={(novo) => {
                setPorPagina(novo);
                setPagina(1);
              }}
            />
          }
        />

        <h2 className="txt-secao text-tinta">Inspeções em andamento</h2>
        <Tabela
          rotulo="Inspeções em andamento"
          colunas={[
            {
              chave: 'equipamento',
              rotulo: 'Máquina',
              celula: (i) => (
                <span className="flex min-w-0 flex-col">
                  <span className="codigo text-tinta">{i.equipamento}</span>
                  <TextoDaCelula
                    texto={`${i.checklist} · ${rotinaEscrita(i.rotina)}`}
                    classe="txt-apoio text-tinta-apagada"
                    linhas={1}
                  />
                </span>
              ),
            },
            { chave: 'dia', rotulo: 'Dia', celula: (i) => <span className="numero text-tinta">{formatarDia(i.dia)}</span> },
            {
              chave: 'respondidos',
              rotulo: 'Respondidos',
              alinhamento: 'numero',
              celula: (i) => <span className="numero text-tinta">{formatarInteiro(i.respondidos)}</span>,
            },
            {
              chave: 'ocorrencias',
              rotulo: 'Achados',
              alinhamento: 'numero',
              celula: (i) => <span className="numero text-tinta">{formatarInteiro(i.ocorrencias)}</span>,
            },
          ]}
          linhas={paginaDasEmAndamento.visiveis}
          rodape={<RodapeTabela {...paginaDasEmAndamento.rodape} />}
          chaveDaLinha={(i) => i.id}
          carregando={inspecoes.isPending}
          erro={
            inspecoes.isError ? (
              <ErroDaLista oQue="As inspeções" erro={inspecoes.error} aoTentarDeNovo={() => void inspecoes.refetch()} />
            ) : null
          }
          alturaDupla
          {...(permissoes.includes('inspecao.gravar')
            ? {
                /* Responder e concluir moram na gaveta: concluir direto da
                   linha recusava com item em branco, sem onde responder. O
                   clique na linha abre a mesma gaveta (p.29). */
                aoSelecionar: (i: (typeof paginaDasEmAndamento.visiveis)[number]) => setRespondendo(i),
                acoes: (i: (typeof paginaDasEmAndamento.visiveis)[number]) => (
                  <Botao nivel="terciario" onClick={() => setRespondendo(i)}>
                    Responder itens
                  </Botao>
                ),
              }
            : {})}
          vazio={
            <EstadoVazio
              titulo="Nenhuma inspeção em andamento"
              descricao="Abra a inspeção de uma máquina para responder o checklist publicado dela."
              acao={
                permissoes.includes('inspecao.gravar') ? (
                  <Botao nivel="secundario" onClick={() => setAbrindo(true)}>
                    Abrir inspeção
                  </Botao>
                ) : undefined
              }
            />
          }
        />

        <h2 className="txt-secao text-tinta">Quem ainda não foi verificado</h2>
        <Tabela
          rotulo="Cobertura da inspeção"
          colunas={COLUNAS_DE_COBERTURA}
          linhas={paginaDaCobertura.visiveis}
          rodape={<RodapeTabela {...paginaDaCobertura.rodape} />}
          chaveDaLinha={(l) => `${l.equipamentoId}-${l.checklistId}`}
          carregando={cobertura.isPending}
          erro={
            cobertura.isError ? (
              <ErroDaLista oQue="As máquinas da cobertura" erro={cobertura.error} aoTentarDeNovo={() => void cobertura.refetch()} />
            ) : null
          }
          alturaDupla
          vazio={
            <EstadoVazio
              titulo="Nenhum checklist publicado para a frota"
              descricao="Monte o checklist de um modelo ou configuração e publique-o com a nota da revisão técnica. Até lá não há o que cobrir."
            />
          }
        />
      </div>

      <RespostasDaInspecao
        inspecao={respondendo}
        aoFechar={() => setRespondendo(null)}
        aoConcluir={(id) => concluir.mutate(id)}
        concluindo={concluir.isPending}
      />

      <Janela
        titulo="Evidência de campo"
        aberta={anexando !== null}
        aoFechar={() => {
          setAnexando(null);
          setDescricaoDaEvidencia('');
          setArquivo(null);
        }}
        acoes={
          <Botao
            nivel="terciario"
            onClick={() => {
              setAnexando(null);
              setDescricaoDaEvidencia('');
              setArquivo(null);
            }}
          >
            Fechar
          </Botao>
        }
      >
        <EvidenciasDaOcorrencia
          ocorrencia={anexando}
          podeAnexar={permissoes.includes('inspecao.gravar')}
          descricao={descricaoDaEvidencia}
          aoMudarDescricao={setDescricaoDaEvidencia}
          arquivo={arquivo}
          aoEscolherArquivo={setArquivo}
        />
      </Janela>

      <Janela
        titulo="Encerrar a ocorrência"
        aberta={encerrando !== null && permissoes.includes('ocorrencia.encerrar')}
        aoFechar={fecharEncerramento}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharEncerramento}>
              Voltar
            </Botao>
            <HistoriaNaMesa codigo="BTN-INS-03" className="flex">
              <Botao
                nivel="secundario"
                disabled={faltaParaEncerrar !== undefined || encerrar.isPending}
                onClick={() => {
                  if (encerrando !== null && desfecho !== null) {
                    encerrar.mutate({ id: encerrando.id, desfecho, ordemId, justificativa });
                  }
                }}
              >
                {encerrar.isPending ? 'Encerrando…' : 'Encerrar ocorrência'}
              </Botao>
            </HistoriaNaMesa>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-corpo text-pretty text-tinta">
            {encerrando === null
              ? ''
              : `${encerrando.equipamento} · ${encerrando.descricao} · ${criticidadeEscrita(encerrando.criticidade).toLowerCase()}, aberta há ${formatarInteiro(encerrando.diasAberta)} ${encerrando.diasAberta === 1 ? 'dia' : 'dias'}.`}
          </p>

          <fieldset className="flex flex-col gap-2 border-0 p-0">
            <legend className="txt-rotulo text-tinta">Como ela termina</legend>
            {DESFECHOS.map((d) => (
              <label key={d} className="flex items-baseline gap-2">
                <input
                  type="radio"
                  name={`${base}-desfecho`}
                  value={d}
                  checked={desfecho === d}
                  onChange={() => setDesfecho(d)}
                />
                <span className="txt-corpo text-tinta">{desfechoEscrito(d)}</span>
              </label>
            ))}
          </fieldset>

          {desfecho === 'ordem_de_servico' ? (
            <>
              <CampoSelecao
                rotulo="Ordem de serviço"
                valor={ordemId}
                aoMudar={setOrdemId}
                opcoes={opcoesDeOrdem}
                rotuloVazio={rotuloDaEscolha({
                  consulta: ordens,
                  pode: podeLerOrdens,
                  quantas: opcoesDeOrdem.length,
                  escolha: 'Escolha a O.S.',
                  nenhuma: 'Nenhuma O.S. aberta desta máquina · abra uma na Oficina e volte aqui',
                  semPermissao: 'O seu perfil não lê as ordens de serviço · peça a liberação em Cadastros › Governança',
                })}
              />
              <p className="txt-ajuda">
                A O.S. aberta desta máquina que vai tratar a ocorrência. É esse elo que fecha o ciclo entre inspeção e ação.
              </p>
            </>
          ) : null}

          <CampoTexto
            rotulo="O que foi feito"
            valor={justificativa}
            aoMudar={setJustificativa}
            ajuda={
              encerrando?.criticidade === 'critico' && desfecho !== 'ordem_de_servico'
                ? 'Obrigatório, com pelo menos 20 caracteres: ocorrência crítica não se encerra sem O.S. nem sem dizer o que foi feito.'
                : 'Opcional, mas é o que explica o encerramento para quem ler depois.'
            }
          />

          {/* O que falta, escrito, em vez de um botão desligado sem razão. */}
          {faltaParaEncerrar === undefined ? null : (
            <p className="txt-apoio text-tinta-secundaria">{faltaParaEncerrar}</p>
          )}
          {encerrar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(encerrar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      {/* O aviso do bloqueio, onde a dúvida aparece: pedir bloqueio não para a
          máquina. A resposta da rota traz o texto, e a tela o mostra. */}
      {sinalizar.data === undefined ? null : (
        <div role="status" className="pagina">
          <p className="txt-apoio text-pretty text-tinta-secundaria">{sinalizar.data.aviso}</p>
        </div>
      )}
    </div>
  );
}

/**
 * As fotos e observações de uma ocorrência — ferramenta 2 do v6.
 *
 * ## Por que o arquivo não é escolhido antes da descrição
 *
 * A descrição vem primeiro na tela porque ela é a parte que as pessoas pulam.
 * A migração a exige, e a razão está escrita nela: sem o que a foto mostra, a
 * pasta de fotos da ocorrência vira um álbum que só quem tirou entende — e a
 * ferramenta pede "fotos E observações", nas duas.
 *
 * ## A evidência não se edita
 *
 * Nem se apaga: ela é prova do que o campo viu, e a tabela é só de inserção.
 * Corrigir é anexar outra, com a descrição dizendo o que mudou. A tela não
 * oferece botão de remover, e isso não é lacuna.
 */
function EvidenciasDaOcorrencia({
  ocorrencia,
  podeAnexar,
  descricao,
  aoMudarDescricao,
  arquivo,
  aoEscolherArquivo,
}: {
  readonly ocorrencia: Ocorrencia | null;
  readonly podeAnexar: boolean;
  readonly descricao: string;
  readonly aoMudarDescricao: (valor: string) => void;
  readonly arquivo: File | null;
  readonly aoEscolherArquivo: (arquivo: File | null) => void;
}) {
  const base = useId();
  const cliente = useQueryClient();

  const lista = useQuery({
    queryKey: ['inspecao', 'evidencias', ocorrencia?.id ?? null],
    queryFn: () => api.evidencias((ocorrencia as Ocorrencia).id),
    enabled: ocorrencia !== null,
  });

  const anexar = useMutation({
    mutationFn: () => api.anexarEvidencia((ocorrencia as Ocorrencia).id, descricao, arquivo as File),
    meta: {
      retorno: retornoDe<{ descricao: string }>((r) => ({
        titulo: 'Evidência anexada',
        ...(ocorrencia ? { alvo: { codigo: ocorrencia.equipamento, nome: ocorrencia.descricao } } : {}),
        fatos: [r.descricao, arquivo?.name ?? null],
      })),
    },
    onSuccess: async () => {
      aoMudarDescricao('');
      aoEscolherArquivo(null);
      await cliente.invalidateQueries({ queryKey: ['inspecao', 'evidencias'] });
    },
  });

  const abrir = useMutation({
    mutationFn: api.urlDaEvidencia,
    /* Abrir não grava nada: a aba nova com o arquivo é o retorno. */
    meta: { silencioso: true },
    onSuccess: (r) => {
      /* A URL vale cinco minutos e vem do armazenamento: abrir em aba nova
         evita que a pessoa perca a fila ao voltar. */
      window.open(r.url, '_blank', 'noopener,noreferrer');
    },
  });

  if (ocorrencia === null) return null;

  return (
    <div className="flex flex-col gap-4">
      <p className="txt-corpo text-pretty text-tinta">
        {`${ocorrencia.equipamento} · ${ocorrencia.descricao}`}
      </p>

      {lista.isPending ? (
        /* Carregando (p.32) na forma da lista: a descrição, a data e o botão
           de abrir à direita. */
        <Carregando oQue="Carregando as evidências">
          <ul aria-hidden="true" className="flex flex-col gap-2">
            {['w-1/2', 'w-2/5'].map((largura) => (
              <li key={largura} className="flex items-center justify-between gap-2 border-b border-borda-sutil pb-2">
                <span className="flex min-w-0 flex-1 flex-col gap-2 py-1">
                  <Barra className={`h-3 ${largura}`} />
                  <Barra className="h-2 w-24" />
                </span>
                <Barra className="h-[var(--controle-m)] w-16 rounded-controle" />
              </li>
            ))}
          </ul>
        </Carregando>
      ) : lista.isError ? (
        <p role="alert" className="txt-apoio text-parada-texto">
          {explicarFalha(lista.error).comoResolver}
        </p>
      ) : (lista.data?.evidencias ?? []).length === 0 ? (
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          Nenhuma evidência anexada. A foto do campo reduz as idas e voltas entre a frente e a
          oficina — é para isso que ela existe.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(lista.data?.evidencias ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline justify-between gap-2 border-b border-borda-sutil pb-2">
              <span className="flex min-w-0 flex-col">
                <span className="txt-corpo text-tinta">{e.descricao}</span>
                <span className="txt-apoio text-tinta-apagada">
                  {`${formatarDia(e.enviadoEm)}${e.enviadoPor === null ? '' : ` · ${e.enviadoPor}`}`}
                </span>
              </span>
              <Botao nivel="terciario" disabled={abrir.isPending} onClick={() => abrir.mutate(e.id)}>
                Abrir
              </Botao>
            </li>
          ))}
        </ul>
      )}

      {abrir.isError ? (
        <p role="alert" className="txt-apoio text-parada-texto">
          {explicarFalha(abrir.error).comoResolver}
        </p>
      ) : null}

      {/* O achado encerrado não recebe evidência: mexer no que alguém fechou
          com justificativa escrita não é corrigir o registro. A tela diz isso
          em vez de mostrar um campo que o servidor recusaria. */}
      {!podeAnexar ? (
        <p className="txt-apoio text-tinta-secundaria">
          Você não tem a licença inspecao.gravar para anexar. Peça a liberação em Cadastros ·
          Governança.
        </p>
      ) : (
        <div className="flex flex-col gap-3 border-t border-borda pt-4">
          <CampoTexto
            rotulo="O que a foto mostra"
            valor={descricao}
            aoMudar={aoMudarDescricao}
            ajuda="Obrigatório: sem isso, a pasta de fotos vira um álbum que só quem tirou entende."
          />
          <HistoriaNaMesa codigo="CMP-INS-04">
            <Campo id={`${base}-arquivo`} rotulo="Arquivo">
              <input
                id={`${base}-arquivo`}
                type="file"
                className="controle"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(ev) => aoEscolherArquivo(ev.target.files?.[0] ?? null)}
              />
            </Campo>
          </HistoriaNaMesa>
          <p className="txt-apoio text-tinta-secundaria">
            Foto em JPEG ou PNG, ou documento em PDF. O tipo é conferido pelo conteúdo do arquivo, e
            não pelo nome dele.
          </p>
          <div>
            <Botao
              nivel="secundario"
              disabled={descricao.trim() === '' || arquivo === null || anexar.isPending}
              onClick={() => anexar.mutate()}
            >
              {anexar.isPending ? 'Anexando…' : 'Anexar evidência'}
            </Botao>
          </div>
          {anexar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(anexar.error).comoResolver}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
